"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Bell } from "lucide-react";

import { NotificationItem } from "@/components/notifications/notification-item";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  useMarkNotificationRead,
  useNotificationPreview,
  useNotificationsRealtime,
  useUnreadNotificationCount,
} from "@/hooks/use-notifications";
import type { AppNotification } from "@/lib/notifications/types";
import { getErrorMessage } from "@/lib/utils";

export function NotificationBell() {
  const router = useRouter();
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const enabled = user?.accountStatus === "active";
  const userId = enabled ? user.id : null;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useNotificationsRealtime(userId, Boolean(userId));

  const previewQuery = useNotificationPreview(Boolean(userId));
  const unreadQuery = useUnreadNotificationCount(Boolean(userId));
  const markRead = useMarkNotificationRead();
  const unreadCount = unreadQuery.data ?? 0;
  const notifications = previewQuery.data ?? [];

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  async function openNotification(notification: AppNotification) {
    setOpen(false);
    if (!notification.isRead) {
      try {
        await markRead.mutateAsync(notification.id);
      } catch {
        // A navigáció akkor is történjen, ha a read state mentése késik.
      }
    }
    router.push(notification.targetPath);
  }

  return (
    <div ref={rootRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Értesítések"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <Bell className="size-4" />
        {unreadCount > 0 ? (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2.5">
            <p className="text-sm font-semibold">Értesítések</p>
            {unreadCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                {unreadCount} olvasatlan
              </span>
            ) : null}
          </div>

          <div className="max-h-96 overflow-y-auto p-1.5">
            {!userId ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Az értesítésekhez aktív fiók szükséges.
              </p>
            ) : previewQuery.isLoading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex gap-3 px-2 py-2">
                    <Skeleton className="size-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : previewQuery.isError ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                {getErrorMessage(previewQuery.error)}
              </p>
            ) : notifications.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Nincs új értesítés.
              </p>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  compact
                  onOpen={openNotification}
                />
              ))
            )}
          </div>

          <div className="border-t px-3 py-2">
            <Link
              href="/notifications"
              className="text-sm font-medium text-primary hover:underline"
              onClick={() => setOpen(false)}
            >
              Összes értesítés
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
